/**
 * `/group` — the two questions a room needs answered, and no others.
 *
 * How many, and how long. Both exist because the one-tap default is genuinely wrong for some
 * real situations: four people meeting at a festival, or a weekend where a room that dies on
 * Saturday morning is worse than no room at all.
 *
 * This screen is deliberately *not* on the one-tap path. "Find each other" still creates a
 * two-person hour without asking anything, and this is the door you take when you know you need
 * something else.
 */

import { MAX_PARTICIPANTS, SESSION_LENGTHS, type SessionLengthId } from '@beeline/shared';

import { el } from '../lib/dom';
import { navigate } from '../lib/navigate';
import type { View } from '../lib/router';
import { withSimParams } from '../sim/peer';

/** Room sizes worth offering. Past eight the rim runs out of room for chevrons. */
const SIZES = [2, 3, 4, 6, MAX_PARTICIPANTS] as const;

export const groupView: View = (container) => {
  let capacity: number = 4;
  let length: SessionLengthId = 'hour';

  const sizeButtons = new Map<number, HTMLElement>();
  const lengthButtons = new Map<SessionLengthId, HTMLElement>();

  const paint = (): void => {
    for (const [value, button] of sizeButtons) {
      button.classList.toggle('choice--on', value === capacity);
      button.setAttribute('aria-pressed', String(value === capacity));
    }
    for (const [value, button] of lengthButtons) {
      button.classList.toggle('choice--on', value === length);
      button.setAttribute('aria-pressed', String(value === length));
    }
  };

  const sizeRow = el(
    'div',
    { class: 'choices' },
    ...SIZES.map((value) => {
      const button = el(
        'button',
        {
          class: 'choice',
          type: 'button',
          onclick: () => {
            capacity = value;
            paint();
          },
        },
        String(value),
      );
      sizeButtons.set(value, button);
      return button;
    }),
  );

  const lengthRow = el(
    'div',
    { class: 'choices' },
    ...SESSION_LENGTHS.map((option) => {
      const button = el(
        'button',
        {
          class: 'choice choice--wide',
          type: 'button',
          onclick: () => {
            length = option.id;
            paint();
          },
        },
        option.label,
      );
      lengthButtons.set(option.id, button);
      return button;
    }),
  );

  container.append(
    el(
      'main',
      { class: 'screen screen--form' },
      el('h1', { class: 'screen__title', text: 'Set up a group' }),
      el('p', {
        class: 'screen__body',
        text: 'Everyone gets the same link. One arrow at a time, and a list of everybody else.',
      }),
      // Said here because it is the answer to what a group actually needs, and because the
      // screen after this one is where it happens — while nobody has arrived yet, rather than
      // after five people have started walking at each other.
      el('p', {
        class: 'screen__body',
        text: 'You can set the meeting point everybody walks to on the next screen.',
      }),

      el(
        'section',
        { class: 'field' },
        el('h2', { class: 'field__label', text: 'How many people' }),
        sizeRow,
        el('p', { class: 'field__hint', text: 'Including you.' }),
      ),

      el(
        'section',
        { class: 'field' },
        el('h2', { class: 'field__label', text: 'How long it lasts' }),
        lengthRow,
        el('p', {
          class: 'field__hint',
          text: 'It ends by itself either way, and everything about it is deleted.',
        }),
      ),

      el(
        'div',
        { class: 'screen__actions' },
        el(
          'button',
          {
            class: 'button button--primary button--block',
            type: 'button',
            'data-action': 'create-group',
            onclick: () => navigate(withSimParams(`/new?capacity=${capacity}&length=${length}`)),
          },
          'Create the room',
        ),
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => navigate('/'),
          },
          'Back',
        ),
      ),
    ),
  );

  paint();
};
